/**
 * The plain parts of Início (docs/refactor/09-inicio.md): what waits first, where the person left
 * each project, and how ready each project is. pages/Dashboard.tsx draws them.
 */

import { readinessOf, type LaunchStage } from './repositoryRun'
import type { InboxItem, LiveSession, WorkOrder } from './types'

/** The inbox's own order: what needs the person, then what failed, ended, waits for their computer. */
const KIND_ORDER: InboxItem['kind'][] = ['needsYou', 'pending', 'failed', 'done', 'waiting', 'team']

/** The first `count` items of the inbox, in the order the inbox shows them (newest first within a kind). */
/** What asks for the person, as the badge counts it (docs/refactor/41, A3). */
export const ASKS_FOR_YOU: Array<InboxItem['kind']> = ['needsYou', 'failed', 'published']

export function firstWaiting(items: InboxItem[], count = 3): InboxItem[] {
  // "Esperando você" holds the card's name to what it shows: only what asks for the person (41, A4).
  return items
    .filter((item) => ASKS_FOR_YOU.includes(item.kind))
    .sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) || b.at.localeCompare(a.at))
    .slice(0, count)
}

/** The latest session in each project, newest first: one click back to where the person left it. */
export function latestPerRepository(sessions: LiveSession[], count = 5): LiveSession[] {
  const seen = new Set<string>()
  const result: LiveSession[] = []
  for (const session of [...sessions].sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt))) {
    const key = (session.repositoryName ?? '').toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    result.push(session)
    if (result.length === count) break
  }
  return result
}

/**
 * "Agora" means now: a session counts while it did something in the last few minutes. An open session
 * that went quiet (a conversation left there) belongs to "Continuar", not to what is running.
 */
export const NOW_WINDOW_MS = 3 * 60_000

/** Sessions still running, of the given agents — or everybody's, for someone with no agents of their own. */
export function runningOf(sessions: LiveSession[], developerIds: Set<string>, now = Date.now()): LiveSession[] {
  return sessions.filter(
    (s) => !s.isTerminal && now - Date.parse(s.lastActivityAt) < NOW_WINDOW_MS && (developerIds.size === 0 || developerIds.has(s.developerId)),
  )
}

export interface ProjectReadiness {
  ready: number | null
  mvp: boolean | null
  stage: LaunchStage | null
  at: string | null
}

/**
 * How ready a project is, from the latest "O que falta" that answered for it: the finished order whose
 * reply carries the VEPHUS-READY line (the run.launch prompt asks for it). Null when there is none.
 */
export function latestReadiness(orders: WorkOrder[], repositoryId: string): ProjectReadiness | null {
  const answered = orders
    .filter((o) => o.repositoryId === repositoryId && o.status === 'Done')
    .map((o) => ({ order: o, text: o.lastReply ?? o.lastReport ?? '' }))
    .filter(({ text }) => /VEPHUS-READY:/i.test(text))
    .sort((a, b) => (b.order.finishedAt ?? b.order.createdAt).localeCompare(a.order.finishedAt ?? a.order.createdAt))
  const latest = answered[0]
  if (!latest) return null
  const { ready, mvp, stage } = readinessOf(latest.text)
  return { ready, mvp, stage, at: latest.order.finishedAt }
}

/** Today, from local midnight to the next, as instants: the window the day's figures are asked for. */
export function todayWindow(now = new Date()): { from: string; to: string; date: string } {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const end = new Date(start)
  end.setDate(end.getDate() + 1)
  const pad = (n: number) => String(n).padStart(2, '0')
  return { from: start.toISOString(), to: end.toISOString(), date: `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}` }
}

const LAST_VISIT_KEY = 'devpulse:lastVisit'
let visitBefore: string | null | undefined

/**
 * When the person was last here, read once per app session and then stamped with now — so Início can open with
 * what the agents did while they were away (docs/refactor/41 §4.1), and a reload does not swallow the recap.
 */
export function lastVisit(): string | null {
  if (visitBefore === undefined) {
    try {
      visitBefore = localStorage.getItem(LAST_VISIT_KEY)
    } catch {
      visitBefore = null
    }
  }
  try {
    localStorage.setItem(LAST_VISIT_KEY, new Date().toISOString())
  } catch {
    // Storage blocked: the next visit simply has no recap.
  }
  return visitBefore
}

export interface AwayRecap {
  done: InboxItem[]
  failed: number
  asks: number
  published: number
}

/** What arrived while the person was away; null when there is nothing to tell (or no earlier visit). */
export function awayRecap(items: InboxItem[], since: string | null): AwayRecap | null {
  if (!since) return null
  const fresh = items.filter((item) => item.at > since)
  if (fresh.length === 0) return null
  const done = fresh.filter((item) => item.kind === 'done').sort((a, b) => b.at.localeCompare(a.at))
  const recap: AwayRecap = {
    done,
    failed: fresh.filter((item) => item.kind === 'failed').length,
    asks: fresh.filter((item) => item.kind === 'needsYou').length,
    published: fresh.filter((item) => item.kind === 'published').length,
  }
  return recap.done.length + recap.failed + recap.asks + recap.published === 0 ? null : recap
}

